import { getBooleanFromString } from '../../utils/string-utils';
import { isMobile, isSafari, isWidthDeviceMobileTablet } from '../../utils/device-utils';
import logger from '../../services/logging/logging-service';
import { initTvCardBox } from '../templating/tv-card-box';
import { removeElementDom } from '../../utils/dom-utils';
import { skyFoundationSettings } from '../../services/configs/configs-service';
import {
  isSectionHome,
  isSubSectionHome,
  isHomePage,
  isEntertainmentDomain,
  isStickyCarouselTemplateEnable,
} from '../../utils/aem-utils';

const PLAYER = 'j-player';
const L_GRID_RAIL = 'l-grid__rail';
const PLAYER_WITH_CAROUSEL_ID = 'j-player-with-carousel';
const PLAYER_CLASS_RAIL = 'j-tv-card'; // if change in this file please change in tv-card-box.js
const PLAYER_TYPE_CARD_RAIL = 'rail';
const PLAYER_CLASS_HEADER = 'c-tv-card__header';
const HERO_PLAYER_CONTAINER = '.c-hero__media-wrapper .j-player';
const STICKY = 'sticky';
const HERO_PLAYER_TYPE = 'hero';
const HERO_HP_PLAYER_TYPE = 'hero - hp';
const BODY = 'body';
const STICKY_TRACK = '-sticky';
const HOOK_CAROUSEL_ELEMENT_STICK = '.j-player-carousel-element';
const SAFARI_PLAYER_CARD_CLASS = 'c-player-card-safari';
const isHomepageInArticle = skyFoundationSettings.getLazyLoadedHomepage();
const DEFAULT_DAI_OFF_VALUE = undefined;
const DEFAULT_SOCIAL_VALUE = undefined;

let heroLivePlayerInitialized = false;
let playerInBodyNumber = 0;
let playerManager;

const addSafariPlayerCardClass = () => {
  if (isSafari()) {
    const players = document.querySelectorAll(`.${PLAYER}`);
    players.forEach((player) => {
      player.classList.add(SAFARI_PLAYER_CARD_CLASS);
    });
  }
};

const mapPlayerCarousel = (player) => {
  player.wasInitialised = player.id !== PLAYER_WITH_CAROUSEL_ID ? true : null;
};

const getPlayerInPageList = (onlyLive) => {
  let playerList = onlyLive
    ? document.querySelectorAll(`.${L_GRID_RAIL} .${PLAYER}`)
    : document.querySelectorAll(`.${PLAYER}`);
  logger.info(` [ INIT - Player ] LISTA PLAYER: ${playerList}`);
  playerList = [...playerList]
    .filter((item) => item.wasInitialised !== true)
    .filter((item) => item.dataset.videoid >= 1);
  playerList.forEach(mapPlayerCarousel);
  if (onlyLive) {
    playerList.forEach((player) => {
      player.onlyLive = onlyLive;
    });
  }
  return playerList;
};

const getPlayerType = (playerType) => {
  if (playerType === BODY) {
    playerInBodyNumber++;
    return `${playerType}-${playerInBodyNumber}`;
  }
  return playerType;
};

const computedLive = (playerData) => {
  if (getBooleanFromString(playerData.dataset.islive) && !heroLivePlayerInitialized) {
    heroLivePlayerInitialized = true;
    return true;
  }
  return false;
};

const clearPlayersLive = () => {
  if (!isMobile() || isHomepageInArticle) {
    const livePlayersNodeList = Array.from(document.querySelectorAll('div[data-islive="true"]'));
    const isLivePlayerInHero = livePlayersNodeList.filter((element) => element.dataset.playertype === HERO_PLAYER_TYPE);
    if (isLivePlayerInHero.length > 0) {
      // is live in hero, delete tv card box not in Entertainment.
      if (!isEntertainmentDomain()) {
        removeElementDom(document.querySelector(`.${PLAYER_CLASS_RAIL}`));
        livePlayersNodeList.forEach((livePlayer) => {
          if (livePlayer.dataset.playertype !== HERO_PLAYER_TYPE) {
            removeElementDom(livePlayer);
          }
        });
      }
    }
  }
};

const getPreviewImg = (previewimg) => {
  if (!previewimg) {
    return true;
  }
  if (previewimg === 'false') {
    return false;
  }
  return previewimg;
};

/**
 * Build player setup object
 * @param {*} playerData dataset of player
 * @param {boolean} isLive for live streaming
 */
const buildPlayerSetup = ({ playerData, isLive, iasTracker, playerType }) => ({
  id: playerData.dataset.videoid || '',
  live: playerData.dataset.islive ? getBooleanFromString(playerData.dataset.islive) : false,
  advOff: playerData.dataset.advoff ? getBooleanFromString(playerData.dataset.advoff) : false,
  domain: 'sky',
  autostart: playerData.dataset.autostart ? getBooleanFromString(playerData.dataset.autostart) : true,
  trackdomain: window.location.hostname || '',
  playertype: playerType,
  preview: getPreviewImg(playerData.dataset.previewimg),
  ias: iasTracker,
  nextVideo: playerData.dataset.nextVideo ? getBooleanFromString(playerData.dataset.nextVideo) : false,
  countdown: playerData.dataset.countdown ? getBooleanFromString(playerData.dataset.countdown) : 5000,
  daiOff: isLive ? getBooleanFromString(skyFoundationSettings.getDaiOff()) : DEFAULT_DAI_OFF_VALUE,
  social: playerData.dataset.social ? getBooleanFromString(playerData.dataset.social) : DEFAULT_SOCIAL_VALUE,
});

/**
 * Build player Configuration depending on playerData
 * @param {*} playerData dataset of player
 */
const playerConfBuilder = (playerData) => {
  const containerid = playerData.id;
  let playerType = playerData.dataset.playertype ? getPlayerType(playerData.dataset.playertype) : '';
  const isLive = computedLive(playerData);
  const isRail = playerType === PLAYER_TYPE_CARD_RAIL;
  const heroPlayerContainer = document.querySelector(HERO_PLAYER_CONTAINER);
  const valueDataset = heroPlayerContainer ? heroPlayerContainer.dataset.islive : 'false';
  const playerTypeIsHero = playerType === 'hero' && isLive === false;
  const playerTypeIsCarousel = playerType.includes('carousel');
  const playerTypeHeroIsNotLive = valueDataset === 'true' || !heroPlayerContainer;
  const iasTracker = Boolean(playerTypeIsHero || (playerTypeIsCarousel && playerTypeHeroIsNotLive));
  const stickyCarouselElement =
    document.querySelector(HOOK_CAROUSEL_ELEMENT_STICK)?.dataset?.stickycarouselactive === 'true';
  const stickyCarouselTemplateTrack = isStickyCarouselTemplateEnable();
  if (stickyCarouselTemplateTrack && stickyCarouselElement && playerTypeIsCarousel) {
    playerType = playerType.concat(STICKY_TRACK);
  }
  if (playerType === PLAYER_TYPE_CARD_RAIL) {
    playerType = STICKY;
  }
  const playersetup = buildPlayerSetup({ playerData, isLive, iasTracker, playerType });

  if ((isMobile() && isRail) || (isWidthDeviceMobileTablet() && isRail)) {
    logger.info(` [ INIT - Player ] is Mobile Player and playerType = ${PLAYER_TYPE_CARD_RAIL}`);
    const tvCardBoxRail = document.querySelector(`.${PLAYER_CLASS_RAIL}`);
    logger.info('[ INIT - Player ] before tv card');
    logger.info('[TV - CARD] before tv card');
    initTvCardBox({ tvCardBoxRail, isLive, isLiveDesktop: false });
    return null;
  }

  if (!isMobile()) {
    logger.info(' [ INIT - Player ] is not mobile');

    if (!isRail) {
      logger.info(' [ INIT - Player ] playerType is not rail');
      playersetup.live = isLive;
    }

    if (isRail && isLive) {
      logger.info(' [ INIT - Player ] playerType is rail');
      playersetup.live = isLive;
      playersetup.playertype = STICKY;
    }

    if ((isRail && !isLive) || (isWidthDeviceMobileTablet() && !isLive && isRail)) {
      logger.info(' [ INIT - Player ] remove video Box Rail');
      const tvCardBoxRail = document.querySelector(`.${PLAYER_CLASS_RAIL}`);
      const tvCardBoxHeader = tvCardBoxRail.querySelector(`.${PLAYER_CLASS_HEADER}`);
      if (!playerData.onlyLive) {
        removeElementDom(tvCardBoxHeader);
      }
      logger.info('[TV - CARD] players tv card');
      initTvCardBox({ tvCardBoxRail, isLiveDesktop: true });
    }
  }

  if (
    (isHomePage() || isSectionHome() || isSubSectionHome()) &&
    !playersetup.autostart &&
    playersetup.playertype === HERO_PLAYER_TYPE
  ) {
    playersetup.playertype = HERO_HP_PLAYER_TYPE;
  }

  logger.info(
    ` [ INIT - Player ] return player config containerId:${containerid} playerSetup:${JSON.stringify(playersetup)}`
  );
  return {
    containerid,
    playersetup,
  };
};

/**
 * Get Player Config List
 */
const getPlayerConfigList = (onlyLive) => {
  return [...getPlayerInPageList(onlyLive)].map(playerConfBuilder).filter((playerConf) => Boolean(playerConf));
};

/**
 * Initialize Sky player Manager
 */
const initSkyPlayerManager = () => {
  if (!playerManager) {
    playerManager = new window.SkyPlayersManager({
      async: true,
      visiblethreshold: 75,
    });
  }
  return playerManager;
};

export const initPlayers = (onlyLive) => {
  addSafariPlayerCardClass();
  clearPlayersLive();
  const playerConfigList = getPlayerConfigList(onlyLive);
  logger.info(` [ INIT - Player ] Players to initialize: ${playerConfigList.length}`);
  return {
    playerList: initSkyPlayerManager().initPlayerList(playerConfigList),
    playerConfigList,
  };
};
